import { IconButton, InputAdornment, Stack, styled, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { InputText } from 'app/components/elements/InputText';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { useAuth } from 'contexts/auth';
import { Role } from 'const/common';
import theme from '../../../styles/theme';
import { useInjection } from '../SettingPage/hook';
import { SettingsShell } from './components/SettingsShell';
import { SettingsContentHeader } from './components/SettingsContentHeader';

export const AccountSettingsPage = () => {
  const { auth } = useAuth();
  const {
    showCurrentPassword,
    setShowCurrentPassword,
    showNewPassword,
    showConfirmNewPassword,
    setShowConfirmNewPassword,
    setShowNewPassword,
    handleSubmit,
    register,
    errors,
    asyncChangePassword,
    isLoading,
    fileInputIconRef,
    handleFileChange,
    fileIcon,
    handleFileClick,
    handleDeleteLogo
  } = useInjection();

  return (
    <SettingsShell activeTabPath="account" breadcrumbLabel="Account Settings">
      <SettingsContentHeader title="Account Settings" />

      <WrapperForm>
        <TextNormalStyled mb={3.75}>Change Password</TextNormalStyled>
        <Stack gap={1} mb={2}>
          <InputText
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                  {showCurrentPassword ? (
                    <Visibility sx={{ color: theme.colors.base }} />
                  ) : (
                    <VisibilityOff sx={{ color: theme.colors.base }} />
                  )}
                </IconButton>
              </InputAdornment>
            }
            placeholder={'Current Password'}
            inputError={errors.currentPassword?.message}
            inputProps={{
              ...register('currentPassword'),
              type: showCurrentPassword ? 'text' : 'password'
            }}
          />
        </Stack>
        <Stack gap={1} mb={2}>
          <InputText
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                  {showNewPassword ? (
                    <Visibility sx={{ color: theme.colors.base }} />
                  ) : (
                    <VisibilityOff sx={{ color: theme.colors.base }} />
                  )}
                </IconButton>
              </InputAdornment>
            }
            placeholder={'New Password'}
            inputError={errors.newPassword?.message}
            inputProps={{
              ...register('newPassword'),
              type: showNewPassword ? 'text' : 'password'
            }}
          />
        </Stack>
        <Stack gap={1} mb={2}>
          <InputText
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}>
                  {showConfirmNewPassword ? (
                    <Visibility sx={{ color: theme.colors.base }} />
                  ) : (
                    <VisibilityOff sx={{ color: theme.colors.base }} />
                  )}
                </IconButton>
              </InputAdornment>
            }
            placeholder={'Confirm New Password'}
            inputError={errors.confirmNewPassword?.message}
            inputProps={{
              ...register('confirmNewPassword'),
              type: showConfirmNewPassword ? 'text' : 'password'
            }}
          />
        </Stack>
        <ButtonCustom
          loading={isLoading}
          onClick={handleSubmit(asyncChangePassword)}
          label={'Save'}
        />
        {(auth?.role === Role.SUPER_ADMIN || auth?.role === Role.ADMIN) && (
          <Stack alignItems={'flex-start'} mt={3} gap={1}>
            <TextNormalStyled>Organization Logo</TextNormalStyled>
            <Stack gap={0.75} alignItems={'flex-end'}>
              <input
                accept="image/*"
                ref={fileInputIconRef}
                hidden
                onChange={(event) => handleFileChange(event)}
                type="file"
              />
              {fileIcon && (
                <img height={80} style={{ objectFit: 'cover' }} src={fileIcon} alt={'icon'} />
              )}
              {fileIcon ? (
                <IconButton onClick={handleDeleteLogo}>
                  <img alt={'icon-user'} src={'/icons/icon_delete.svg'} width={24} />
                </IconButton>
              ) : (
                <IconButton onClick={handleFileClick}>
                  <Stack
                    alignItems={'center'}
                    justifyContent={'center'}
                    bgcolor={'#D9D9D9'}
                    padding={1.8}
                    sx={{ cursor: 'pointer' }}>
                    <img alt={'icon-user'} src={'/icons/icon_logo_upload.svg'} width={53} />
                  </Stack>
                </IconButton>
              )}
            </Stack>
            <Typography sx={{ color: '#53627A', fontSize: 13 }}>
              Upload the logo displayed for your organization.
            </Typography>
          </Stack>
        )}
      </WrapperForm>
    </SettingsShell>
  );
};

const WrapperForm = styled(Stack)(() => ({
  width: '100%',
  maxWidth: '370px'
}));
const TextNormalStyled = styled(Typography)(({ theme }) => ({
  color: theme.colors.base,
  fontSize: '20px',
  fontWeight: 600,
  fontFamily: `'Avenir',sans-serif`
}));
